import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { LocalAgentService, TokenStatusItem } from '../../core/services/local-agent.service';
import { AgentAvailabilityService } from '../../core/services/agent-availability.service';
import {
  TokensafeService,
  ValidacionSolicitudRespuesta,
  ValidacionCorreoRespuesta,
  OtpValidarRespuesta
} from '../../core/services/tokensafe.service';
import { maskEmail } from '../../core/utils/cert-subject.utils';
import { environment } from '../../../environments/environment';
import Swal from 'sweetalert2';
import { NotificationService } from '../../services/notification.service';
import { UiHelperService } from '../../services/ui-helper.service';
import { TokenContentResultModalComponent } from '../token-content-result-modal/token-content-result-modal.component';
import { TokenFisicoAlertModalComponent, TokenFisicoAlertModalData } from '../token-fisico-alert-modal/token-fisico-alert-modal.component';

/** Paso actual del flujo (0 = agente no disponible; 1..5 = pantallas). */
export type PasoDescargarCert = 0 | 1 | 2 | 3 | 4 | 5;

@Component({
  selector: 'app-descargar-certificado',
  templateUrl: './descargar-certificado.component.html',
  styleUrls: ['./descargar-certificado.component.css'],
  standalone: false
})
export class DescargarCertificadoComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  /** Paso 0: complemento no detectado */
  statusLoading = false;
  agentUnavailable = false;

  /** Paso actual cuando el agente está disponible (1-5) */
  paso: PasoDescargarCert = 1;

  /** Pantalla 1 */
  numSolicitud = '';
  validando = false;
  errorPantalla: string | null = null;
  correo = '';           // correo completo desde validación (para OTP)
  correoEnmascarado = '';

  /** Pantalla 2: usuario escribe el correo que corresponde al enmascarado */
  correoDigitado = '';   // correo que el usuario escribe (se envía a otp/solicitar)
  enviandoOtp = false;
  mensajeExito: string | null = null;  // mensaje de éxito al enviar OTP (o el que devuelva el back)
  transacion = '';       // transactionId desde otp/solicitar

  /** Pantalla 3: OTP (mínimo 5 dígitos) + cuenta regresiva */
  readonly otpMinLength = 5;
  readonly otpMaxLength = 10;
  codigoOtp = '';
  validandoOtp = false;
  /** Segundos hasta que expire el OTP (por defecto 5 min; el back podría devolverlo en otp/solicitar). */
  otpExpiresInSeconds = 300;
  remainingSeconds = 0;
  otpExpired = false;
  private otpIntervalId: ReturnType<typeof setInterval> | null = null;
  private otpStartTime = 0;
  token = '';            // JWT desde otp/validar
  solicitante = '';
  cedula = '';
  nit = '';

  /** Pantalla 4 */
  pinUsuario = '';
  pinConfirmacion = '';
  /** Mostrar PIN 1 mientras el usuario mantiene pulsado el ojo */
  showPin1 = false;
  /** Mostrar PIN 2 mientras el usuario mantiene pulsado el ojo */
  showPin2 = false;
  preparando = false;
  sesionPreparada = false;

  /** Pantalla 5: detección de token */
  tokenPaso5Loading = false;
  /** Lista de todos los tokens detectados en paso 5. */
  tokensPaso5: TokenStatusItem[] = [];
  /** Checkbox "De acuerdo, una sola vez" */
  acuerdoDescargar = false;
  /** Pantalla 5: fase del proceso descargar → importar */
  descargando = false;
  /** 'descargando' = descargando certificado; 'importando' = ya descargado, importando en token */
  faseDescargaImportar: 'descargando' | 'importando' | null = null;
  descargaOk = false;
  importacionOk = false;
  /** true mientras se consulta el contenido del token para validar importación */
  validandoContenido = false;

  constructor(
    private localAgentService: LocalAgentService,
    private agentAvailability: AgentAvailabilityService,
    private tokensafeService: TokensafeService,
    private notificationService: NotificationService,
    private uiHelper: UiHelperService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.agentAvailability.isAvailable$.pipe(takeUntil(this.destroy$)).subscribe((ok) => {
      if (!ok) {
        this.agentUnavailable = true;
        this.statusLoading = false;
      }
    });
    this.loadTokenStatus();
  }

  ngOnDestroy(): void {
    this.stopOtpCountdown();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Inicia la cuenta regresiva del OTP al entrar en pantalla 3. */
  startOtpCountdown(): void {
    this.stopOtpCountdown();
    this.otpExpired = false;
    this.remainingSeconds = Math.max(0, this.otpExpiresInSeconds);
    this.otpStartTime = Date.now();
    this.otpIntervalId = setInterval(() => this.otpTick(), 1000);
  }

  private stopOtpCountdown(): void {
    if (this.otpIntervalId) {
      clearInterval(this.otpIntervalId);
      this.otpIntervalId = null;
    }
  }

  private otpTick(): void {
    const elapsed = Math.floor((Date.now() - this.otpStartTime) / 1000);
    this.remainingSeconds = Math.max(0, this.otpExpiresInSeconds - elapsed);
    if (this.remainingSeconds === 0) {
      this.otpExpired = true;
      this.stopOtpCountdown();
    }
  }

  get countdownLabel(): string {
    const m = Math.floor(this.remainingSeconds / 60);
    const s = this.remainingSeconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  get countdownFriendlyMessage(): string {
    const m = Math.floor(this.remainingSeconds / 60);
    const s = this.remainingSeconds % 60;
    if (m > 0 && s > 0) return `${m} min ${s} s`;
    if (m > 0) return `${m} min`;
    return `${s} s`;
  }

  readonly ringCircumference = 2 * Math.PI * 36;

  get ringDashOffset(): number {
    return this.ringCircumference * (1 - this.otpProgressPercent / 100);
  }

  get otpProgressPercent(): number {
    if (this.otpExpiresInSeconds <= 0) return 0;
    return Math.round((this.remainingSeconds / this.otpExpiresInSeconds) * 100);
  }

  get countdownState(): 'ok' | 'warning' | 'critical' | 'expired' {
    if (this.otpExpired) return 'expired';
    if (this.remainingSeconds < 30) return 'critical';
    if (this.remainingSeconds < 60) return 'warning';
    return 'ok';
  }

  get canSubmitOtp(): boolean {
    const code = (this.codigoOtp ?? '').trim().replace(/\D/g, '');
    return !this.otpExpired && !this.validandoOtp && code.length >= this.otpMinLength;
  }

  /** Proceso async en curso: bloquea “Empezar de nuevo” / “Ir al inicio”. */
  get procesoEnCurso(): boolean {
    return !!(
      this.validando ||
      this.enviandoOtp ||
      this.validandoOtp ||
      this.preparando ||
      this.tokenPaso5Loading ||
      this.descargando ||
      this.validandoContenido
    );
  }

  get showNotAvailableView(): boolean {
    return this.statusLoading || this.agentUnavailable;
  }

  get showFlujo(): boolean {
    return !this.statusLoading && !this.agentUnavailable;
  }

  get pasoActual(): PasoDescargarCert {
    return this.paso;
  }

  loadTokenStatus(): void {
    this.statusLoading = true;
    this.agentUnavailable = false;
    this.agentAvailability
      .refreshOnce()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ok) => {
          if (!ok) {
            this.agentUnavailable = true;
            this.statusLoading = false;
            return;
          }
          this.localAgentService.getTokenStatus()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => { this.statusLoading = false; },
              error: () => {
                this.agentUnavailable = true;
                this.statusLoading = false;
              }
            });
        },
        error: () => {
          this.agentUnavailable = true;
          this.statusLoading = false;
        }
      });
  }

  retryStatus(): void { this.loadTokenStatus(); }

  get agentDownloadUrl(): string {
    return environment.agentDownloadUrl ?? '';
  }

  get agentReleaseLabel(): string {
    return `${environment.agentDisplayName} — ${environment.agentReleaseLabel}`;
  }

  openAgentDownload(): void {
    const url = environment.agentDownloadUrl;
    if (url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        Swal.fire({
          icon: 'info',
          title: 'Ruta copiada',
          html:
            `La ruta del complemento se ha copiado al portapapeles.<br><br>` +
            `<strong>${this.agentReleaseLabel}</strong><br><br>` +
            'Ábrela en el Explorador de archivos y ejecuta el instalador.',
          confirmButtonText: 'Entendido',
          ...this.notificationService.getSwalBaseConfig()
        });
      }).catch(() => this.showAgentLocalPathFallback(url));
    } else {
      this.showAgentLocalPathFallback(url);
    }
  }

  private showAgentLocalPathFallback(url: string): void {
    Swal.fire({
      icon: 'info',
      title: 'Complemento',
      html: `<strong>${this.agentReleaseLabel}</strong><br><br>Ruta del complemento:<br><br><code>${url}</code>`,
      confirmButtonText: 'Entendido',
      ...this.notificationService.getSwalBaseConfig()
    });
  }

  private setError(msg: string): void {
    this.errorPantalla = msg;
  }

  private clearError(): void {
    this.errorPantalla = null;
  }

  /** Pantalla 1: Validar solicitud → mostrar correo + Enviar OTP. Back: numSolicitud obligatorio. */
  validarSolicitud(): void {
    const num = (this.numSolicitud ?? '').trim();
    if (!num) {
      this.setError('El número de solicitud es obligatorio.');
      return;
    }
    this.clearError();
    this.validando = true;
    this.uiHelper.showSpinner('Validando solicitud…', 'admin');
    this.tokensafeService.validarSolicitud(num)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ValidacionSolicitudRespuesta) => {
          this.validando = false;
          this.uiHelper.hideSpinner();
          this.correo = (res.correoSolicitud ?? res.correo) ?? '';
          this.correoEnmascarado = maskEmail(this.correo, 6);
          this.paso = 2;
        },
        error: (err: unknown) => {
          this.validando = false;
          this.uiHelper.hideSpinner();
          this.setError(this.getMensajeError(err) ?? 'Error al validar la solicitud.');
        }
      });
  }

  /** Pantalla 2: Validar correo. Back: numSolicitud y correoSolicitud obligatorios. */
  enviarOtp(): void {
    const num = (this.numSolicitud ?? '').trim();
    const correo = (this.correoDigitado ?? '').trim();
    if (!num) {
      this.setError('El número de solicitud es obligatorio.');
      return;
    }
    if (!correo) {
      this.setError('El correo electrónico es obligatorio.');
      return;
    }
    this.clearError();
    this.mensajeExito = null;
    this.enviandoOtp = true;
    this.uiHelper.showSpinner('Enviando código de verificación…', 'admin');
    this.tokensafeService.validarCorreo(num, correo)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ValidacionCorreoRespuesta) => {
          this.enviandoOtp = false;
          this.uiHelper.hideSpinner();
          this.transacion = (res.transacion ?? res.idTransaccion ?? res.transactionId ?? res.transaction ?? '') as string;
          if (!this.transacion) {
            this.setError('No se recibió el identificador de transacción.');
            return;
          }
          const msg = res['mensaje'];
          this.mensajeExito = typeof msg === 'string' ? msg : 'Se ha enviado el código OTP a su correo.';
          this.paso = 3;
          this.codigoOtp = '';
          this.startOtpCountdown();
        },
        error: (err: unknown) => {
          this.enviandoOtp = false;
          this.uiHelper.hideSpinner();
          this.setError(this.getMensajeError(err) ?? 'Error al validar correo.');
        }
      });
  }

  /** Pantalla 3: Validar OTP. Back: numSolicitud, transacion y codigoOtp obligatorios. */
  validarOtp(): void {
    const num = (this.numSolicitud ?? '').trim();
    const transacion = (this.transacion ?? '').trim();
    const codigo = (this.codigoOtp ?? '').trim().replace(/\D/g, '').slice(0, this.otpMaxLength);
    if (!num) {
      this.setError('El número de solicitud es obligatorio.');
      return;
    }
    if (!transacion) {
      this.setError('Falta el identificador de transacción. Vuelve al paso anterior.');
      return;
    }
    if (codigo.length < this.otpMinLength) {
      this.setError(`El código OTP es obligatorio y debe tener al menos ${this.otpMinLength} dígitos.`);
      return;
    }
    this.clearError();
    this.validandoOtp = true;
    this.uiHelper.showSpinner('Validando código de verificación…', 'admin');
    this.tokensafeService.validarOtp(num, transacion, codigo)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: OtpValidarRespuesta) => {
          this.validandoOtp = false;
          this.uiHelper.hideSpinner();
          this.token = res.token ?? '';
          this.solicitante = res.solicitante ?? '';
          this.cedula = res.cedula ?? '';
          this.nit = res.nit ?? '';
          if (!this.token) {
            this.setError('No se recibió el token de sesión.');
            return;
          }
          this.stopOtpCountdown();
          this.paso = 4;
          this.pinUsuario = '';
          this.pinConfirmacion = '';
        },
        error: (err: unknown) => {
          this.validandoOtp = false;
          this.uiHelper.hideSpinner();
          this.setError(this.getMensajeError(err) ?? 'Error al validar OTP.');
        }
      });
  }

  onOtpCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const v = input.value.replace(/\D/g, '').slice(0, this.otpMaxLength);
    input.value = v;
    this.codigoOtp = v;
  }

  /** true solo si ambos PIN tienen 4 dígitos y coinciden (habilita Continuar en paso 4). */
  get canSubmitPin(): boolean {
    const pin = (this.pinUsuario ?? '').trim();
    const conf = (this.pinConfirmacion ?? '').trim();
    return pin.length === 4 && /^\d{4}$/.test(pin) && conf.length === 4 && /^\d{4}$/.test(conf) && pin === conf;
  }

  /** true cuando ambos PIN tienen 4 dígitos pero no coinciden (mostrar mensaje al usuario). */
  get pinMismatch(): boolean {
    const pin = (this.pinUsuario ?? '').trim();
    const conf = (this.pinConfirmacion ?? '').trim();
    return pin.length === 4 && /^\d{4}$/.test(pin) && conf.length === 4 && /^\d{4}$/.test(conf) && pin !== conf;
  }

  /** Pantalla 4: Validar PIN y pasar al último paso (sin llamada al back; el PIN se usa en import-pfx). */
  prepararSesion(): void {
    const pin = (this.pinUsuario ?? '').trim();
    const conf = (this.pinConfirmacion ?? '').trim();
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      this.setError('El PIN debe tener 4 dígitos.');
      return;
    }
    if (pin !== conf) {
      this.setError('Los PIN no coinciden.');
      return;
    }
    this.clearError();
    this.sesionPreparada = true;
    this.paso = 5;
    this.acuerdoDescargar = false;
    this.loadTokenStatusPaso5();
  }

  /** Carga GET /api/token/status solo para el paso 5 (ventana de detección de token). */
  loadTokenStatusPaso5(): void {
    this.tokenPaso5Loading = true;
    this.tokensPaso5 = [];
    this.uiHelper.showSpinner('Detectando Certifirma Digital Físico…', 'admin');
    this.localAgentService.getTokenStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.tokenPaso5Loading = false;
          this.uiHelper.hideSpinner();
          this.tokensPaso5 = res?.tokens ?? [];
        },
        error: () => {
          this.tokenPaso5Loading = false;
          this.uiHelper.hideSpinner();
          this.tokensPaso5 = [];
        }
      });
  }

  /** Un solo token cuando la lista tiene exactamente 1; si no, null. */
  get tokenPaso5(): TokenStatusItem | null {
    return this.tokensPaso5.length === 1 ? this.tokensPaso5[0] : null;
  }

  /** true cuando hay exactamente un token y su PIN no está bloqueado. */
  get tokenPaso5PinOk(): boolean {
    const t = this.tokenPaso5;
    return !!t && !t.isUserPinLocked;
  }

  /** Texto de la línea meta de un token (Slot · fabricante · modelo · serie · PIN). */
  tokenMeta(t: TokenStatusItem): string {
    const parts: string[] = [`Slot ${t.slotId}`, t.manufacturer || '', t.model || ''].filter(Boolean);
    if (t.serialNumber) parts.push(`Serie ${t.serialNumber}`);
    parts.push(t.isUserPinLocked ? 'PIN bloqueado' : 'PIN OK');
    return parts.join(' · ');
  }

  get canDescargarPaso5(): boolean {
    return this.tokenPaso5PinOk && this.acuerdoDescargar && !this.descargando && !this.importacionOk;
  }

  /** Pantalla 5: Descargar e importar (gasta la solicitud). La confirmación es el toggle en la pantalla; no se muestra modal. */
  descargarEImportar(): void {
    this.ejecutarDescargaEImportacion();
  }

  /** Tras importación exitosa: consultar contenido del token con el PIN usado y mostrarlo en el modal (validar importación). */
  validarImportacion(): void {
    const token = this.tokenPaso5;
    const pin = (this.pinUsuario ?? '').trim();
    if (!token) {
      this.dialog.open(TokenFisicoAlertModalComponent, {
        width: '440px',
        maxWidth: '95vw',
        disableClose: false,
        panelClass: ['custom-dialog', 'verify-dialog-responsive'],
        data: {
          type: 'warning',
          title: 'Token no disponible',
          message: 'No hay información del token. Vaya a Administración de Certifirma Digital Físico para ver el contenido.'
        } as TokenFisicoAlertModalData
      });
      return;
    }
    if (pin.length < 4) {
      this.dialog.open(TokenFisicoAlertModalComponent, {
        width: '440px',
        maxWidth: '95vw',
        disableClose: false,
        panelClass: ['custom-dialog', 'verify-dialog-responsive'],
        data: {
          type: 'warning',
          title: 'PIN requerido',
          message: 'No se puede validar: falta el PIN del token.'
        } as TokenFisicoAlertModalData
      });
      return;
    }
    this.validandoContenido = true;
    this.clearError();
    this.uiHelper.showSpinner('Validando importación en el token…', 'admin');
    this.localAgentService.getTokenContent(pin, token.slotId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.validandoContenido = false;
          this.uiHelper.hideSpinner();
          if (res.success && res.certificates != null) {
            this.dialog.open(TokenContentResultModalComponent, {
              width: '640px',
              maxWidth: '95vw',
              maxHeight: '90vh',
              data: {
                message: res.message || 'Contenido del token. Certificado importado correctamente.',
                certificates: res.certificates,
                tokenLabel: res.tokenDetails?.label ?? token.label,
                tokenDetails: res.tokenDetails ?? null
              },
              panelClass: ['custom-dialog', 'verify-dialog-responsive']
            });
          } else {
            this.dialog.open(TokenFisicoAlertModalComponent, {
              width: '440px',
              maxWidth: '95vw',
              disableClose: false,
              panelClass: ['custom-dialog', 'verify-dialog-responsive'],
              data: {
                type: 'warning',
                title: 'Sin contenido',
                message: res.message || 'No se obtuvo contenido del token.'
              } as TokenFisicoAlertModalData
            });
          }
        },
        error: (err) => {
          this.validandoContenido = false;
          this.uiHelper.hideSpinner();
          const msg = err?.error?.message ?? err?.message ?? 'No se pudo consultar el contenido del token.';
          this.dialog.open(TokenFisicoAlertModalComponent, {
            width: '440px',
            maxWidth: '95vw',
            disableClose: false,
            panelClass: ['custom-dialog', 'verify-dialog-responsive'],
            data: { type: 'error', title: 'Error al validar', message: msg } as TokenFisicoAlertModalData
          });
        }
      });
  }

  private ejecutarDescargaEImportacion(): void {
    this.clearError();
    const numSolicitud = (this.numSolicitud ?? '').trim();
    const transacion = (this.transacion ?? '').trim();
    const codigoOtp = (this.codigoOtp ?? '').trim().replace(/\s/g, '');
    const token = (this.token ?? '').trim();
    const pinUsuario = (this.pinUsuario ?? '').trim();

    if (!numSolicitud) {
      this.setError('Falta el número de solicitud. No se puede continuar.');
      return;
    }
    if (!transacion) {
      this.setError('Falta el identificador de transacción. Vuelve a validar el correo.');
      return;
    }
    if (!codigoOtp) {
      this.setError('Falta el código OTP. Vuelve a validar el código.');
      return;
    }
    if (!token) {
      this.setError('Falta el token de sesión. Vuelve a validar el OTP.');
      return;
    }
    if (pinUsuario.length !== 4 || !/^\d{4}$/.test(pinUsuario)) {
      this.setError('El PIN del token es obligatorio (4 dígitos).');
      return;
    }

    this.descargando = true;
    this.faseDescargaImportar = 'descargando';
    this.uiHelper.showSpinner('Descargando certificado…', 'download');
    this.tokensafeService.descargarGuardar({
      numSolicitud,
      transacion,
      codigoOtp,
      token
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.descargaOk = true;
          this.faseDescargaImportar = 'importando';
          this.uiHelper.updateSpinnerMessage('Importando certificado en el token…', 'admin', 'Importando en token');
          this.localAgentService.importPfx({
            userPin: pinUsuario,
            numSolicitud,
            transacion,
            token
          })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (importRes) => {
                this.descargando = false;
                this.faseDescargaImportar = null;
                this.uiHelper.hideSpinner();
                if (importRes?.success !== true) {
                  this.setError(
                    importRes?.errorMessage || importRes?.errorCode || 'No se pudo importar el certificado en el token.'
                  );
                  return;
                }
                this.importacionOk = true;
              },
              error: (err: unknown) => {
                this.descargando = false;
                this.faseDescargaImportar = null;
                this.uiHelper.hideSpinner();
                const msg = this.getMensajeError(err);
                this.setError(
                  msg
                    ? `El certificado se descargó correctamente, pero no se pudo importar en el token: ${msg}`
                    : 'El certificado se descargó, pero falló la importación en el token.'
                );
              }
            });
        },
        error: (err: unknown) => {
          this.descargando = false;
          this.faseDescargaImportar = null;
          this.uiHelper.hideSpinner();
          this.setError(this.getMensajeError(err) ?? 'Error al descargar y guardar el certificado.');
        }
      });
  }

  /** Extrae el mensaje de error del backend (mensaje) o de la respuesta HTTP. */
  private getMensajeError(err: unknown): string | null {
    if (typeof err === 'string') return err || null;
    const e = err as { error?: { mensaje?: string }; message?: string };
    return e?.error?.mensaje ?? e?.message ?? null;
  }

  /** Reiniciar flujo desde el paso 1 */
  reiniciarFlujo(): void {
    this.paso = 1;
    this.numSolicitud = '';
    this.correo = '';
    this.correoEnmascarado = '';
    this.correoDigitado = '';
    this.mensajeExito = null;
    this.transacion = '';
    this.codigoOtp = '';
    this.stopOtpCountdown();
    this.remainingSeconds = 0;
    this.otpExpired = false;
    this.token = '';
    this.pinUsuario = '';
    this.pinConfirmacion = '';
    this.sesionPreparada = false;
    this.descargaOk = false;
    this.importacionOk = false;
    this.faseDescargaImportar = null;
    this.tokensPaso5 = [];
    this.tokenPaso5Loading = false;
    this.validandoContenido = false;
    this.acuerdoDescargar = false;
    this.clearError();
  }
}
